import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(undefined);

const STORAGE_KEY = 'flexcoa-theme-v2';
// Pre-FECOA-1827 key. Old builds auto-persisted their 'light' default on first
// load, so a stored 'light' there was usually never an explicit choice.
const LEGACY_STORAGE_KEY = 'flexcoa-theme';

function getSystemPreference() {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getStoredPreference() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    // One-time migration: carry over only dark/system from the legacy key —
    // legacy 'light' can't be distinguished from the old auto-persisted
    // default, so it falls through to the new 'system' default.
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy === 'dark' || legacy === 'system') return legacy;
  } catch {
    // localStorage unavailable
  }
  return null;
}

function applyThemeClass(resolvedTheme) {
  const root = document.documentElement;
  if (resolvedTheme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(() => getStoredPreference() || 'system');
  const [resolvedTheme, setResolvedTheme] = useState(() => {
    const pref = getStoredPreference() || 'system';
    return pref === 'system' ? getSystemPreference() : pref;
  });

  const resolve = useCallback((pref) => {
    return pref === 'system' ? getSystemPreference() : pref;
  }, []);

  useEffect(() => {
    const resolved = resolve(preference);
    setResolvedTheme(resolved);
    applyThemeClass(resolved);
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // localStorage unavailable
    }
  }, [preference, resolve]);

  useEffect(() => {
    if (preference !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const resolved = getSystemPreference();
      setResolvedTheme(resolved);
      applyThemeClass(resolved);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [preference]);

  useEffect(() => {
    applyThemeClass(resolvedTheme);
  }, []);

  const setTheme = useCallback((newPref) => {
    if (newPref === 'light' || newPref === 'dark' || newPref === 'system') {
      setPreference(newPref);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setPreference((prev) => {
      const resolved = resolve(prev);
      return resolved === 'dark' ? 'light' : 'dark';
    });
  }, [resolve]);

  return (
    <ThemeContext.Provider value={{ theme: preference, resolvedTheme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
